import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FieldValueInterface,
  FormInputComponent,
  InlineEditService,
  ObjectType,
  StandardIds,
  SyncIconComponent,
} from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { of, shareReplay, switchMap, tap } from 'rxjs';
import {
  PartnerCRMSalespersonService,
  salespersonFilterFn,
  SalespersonWithDisplayName,
} from '../../salesperson.service';

const NO_VALUE_FOR_SALESPERSON = '<NO_VALUE_FOR_SALESPERSON>';

@Component({
  templateUrl: './additional-salespeople-form-input.component.html',
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    GalaxyBadgeModule,
    GalaxyTooltipModule,
    SyncIconComponent,
  ],
  styleUrls: ['../shared.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AdditionalSalespeopleFormInputComponent extends FormInputComponent {
  @ViewChild('salespeopleInput') salespeopleInput: ElementRef<HTMLInputElement>;

  NO_VALUE_FOR_SALESPERSON = NO_VALUE_FOR_SALESPERSON;

  readOnly = false;
  objectType: ObjectType = 'Contact';
  _control: FormControl<string[]> = new FormControl<string[]>([], {
    nonNullable: true,
  });
  _group: UntypedFormGroup;
  searchControl: FormControl<SalespersonWithDisplayName | string> = new FormControl<
    SalespersonWithDisplayName | string
  >('', {
    nonNullable: true,
  });
  separatorKeysCodes: number[] = [ENTER, COMMA];
  _label = '';

  searchTerm = signal<SalespersonWithDisplayName | string>('');
  selectedSalespeopleIDs = signal<string[]>([]);
  selectedSalespeopleIDs$ = toObservable(this.selectedSalespeopleIDs);
  salespeopleData$ = this.selectedSalespeopleIDs$.pipe(
    switchMap((selectedSalespeople) => {
      if (!selectedSalespeople?.length) {
        return of([]);
      }
      return this.salespersonService.getMultiSalesperson(selectedSalespeople);
    }),
    shareReplay(1),
  );
  salespeopleData = toSignal(this.salespeopleData$);
  set control(control: UntypedFormControl) {
    this._control = control as FormControl<string[]>;
    this._control.markAsPristine();
    this._control.setValue(this._control.value || []);
    this._control.clearValidators();
    this._control.addValidators(this.isSalespersonValid.bind(this));
    this.selectedSalespeopleIDs.set(this._control.value);
    this._control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newValue) => this.selectedSalespeopleIDs.set(newValue || []));
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  private getSalespersonFieldId(): string {
    if (this.objectType === 'Contact') {
      return StandardIds.ContactPrimarySalespersonID;
    } else {
      return StandardIds.CompanyPrimarySalespersonID;
    }
  }

  private isSalespersonValid(_: AbstractControl): ValidationErrors | null {
    const salespersonIds = this.selectedSalespeopleIDs();
    const availableOptionsIds = this.availableOptions().map((s) => s.salespersonId);
    const invalidSalesperson = !!salespersonIds.find((s) => !availableOptionsIds.includes(s));
    if (invalidSalesperson && salespersonIds.length > 0) {
      return {
        invalidSalesperson: true,
      };
    }
    return null;
  }

  set formGroup(group: UntypedFormGroup) {
    this._group = group;
    group.addValidators((control) => {
      const path = this.getSalespersonFieldId();
      const salespersonControl = control.get(path);
      const additionalSalespeopleControl = control.get(this._fieldId());
      if (salespersonControl && additionalSalespeopleControl) {
        const additionalSalespeopleValue: string[] = additionalSalespeopleControl.value;
        const salespersonValue: string | SalespersonWithDisplayName = salespersonControl.value;
        const salespersonID = typeof salespersonValue === 'string' ? salespersonValue : salespersonValue?.salespersonId;
        if (salespersonValue && additionalSalespeopleValue) {
          if (additionalSalespeopleValue.includes(salespersonID)) {
            return {
              additionalSalespeopleInSalesperson: true,
            };
          }
        }
      }
      return null;
    });
  }

  get formGroup(): UntypedFormGroup {
    return this._group;
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    return {
      fieldId: this._fieldId(),
      stringValues: {
        values: this._control.value,
      },
    };
  }

  loadingValues = signal(true);
  availableOptions = signal<SalespersonWithDisplayName[]>([]);
  filteredOptions: Signal<SalespersonWithDisplayName[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.salespersonId;
    return availableOptions.filter((salesperson) => salespersonFilterFn(salesperson, filterTerm));
  });
  selectedSalespeople = computed(() => {
    const selectedIds = this.selectedSalespeopleIDs();
    const salespeopleData = this.salespeopleData();
    const selectedSalespeople = selectedIds.map((id) => {
      const salesperson = salespeopleData?.find((option) => option?.salespersonId === id);
      if (salesperson) {
        return salesperson;
      }
      return { displayName: id, salespersonId: id };
    });
    return selectedSalespeople;
  });
  salespeopleNames = computed(() => {
    const salespeople = this.selectedSalespeople();
    return salespeople.map((salesperson) => salesperson.displayName);
  });

  constructor(
    private readonly salespersonService: PartnerCRMSalespersonService,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
  ) {
    super();
    this.salespersonService.allSalespeople$
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loadingValues.set(true)),
      )
      .subscribe((allSalespeople) => {
        this.availableOptions.set(allSalespeople);
        this.loadingValues.set(false);
        this._control.updateValueAndValidity();
      });
    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((newValue) => this.searchTerm.set(newValue));
  }

  displayFn(
    availableOptions: SalespersonWithDisplayName[],
  ): (salesperson: SalespersonWithDisplayName | string) => string {
    return (salesperson: SalespersonWithDisplayName | string): string => {
      if (!salesperson) {
        return '';
      }
      if (typeof salesperson === 'string') {
        const foundSalesperson = availableOptions.find((sp) => sp.salespersonId === salesperson);
        return foundSalesperson?.displayName || salesperson;
      }
      return salesperson?.displayName;
    };
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const currentSalespeople = this._control.value;
    const hasSalesperson = currentSalespeople.some((sp) => sp === event.option.value);
    if (event.option.value && !hasSalesperson) {
      this._control.patchValue([...currentSalespeople, event.option.value]);
      this._control.markAsDirty();
    }
    this.salespeopleInput.nativeElement.value = '';
    this.searchControl.setValue('');
  }

  add(event: MatChipInputEvent) {
    const currentSalespeople = this._control.value;
    const hasSalesperson = currentSalespeople.some((sp) => sp === event?.value);
    if (event?.value && !hasSalesperson) {
      this._control.patchValue([...currentSalespeople, event.value]);
      this._control.markAsDirty();
    }

    // Clear the input value
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    event.chipInput!.clear();
    this.searchControl.setValue('');
  }

  remove(salespersonId: string) {
    const currentSalespeople = this._control.value;
    const listWithoutSelectedSalesperson = currentSalespeople.filter((sp) => sp !== salespersonId);
    this._control.patchValue([...listWithoutSelectedSalesperson]);
    this._control.markAsDirty();
    this._control.updateValueAndValidity();
  }

  saveField(): void {
    if (!this._control.errors && !this._group.errors?.additionalSalespeopleInSalesperson) {
      // attempt saving salesperson field as well, if there are no changes it won't save
      const salespersonFieldValue: FieldValueInterface = { fieldId: this.getSalespersonFieldId() };
      const salespersonFormControlValue: SalespersonWithDisplayName | string =
        this._group?.value[this.getSalespersonFieldId()];
      if (typeof salespersonFormControlValue === 'string') {
        salespersonFieldValue.stringValue = salespersonFormControlValue;
      } else {
        salespersonFieldValue.stringValue = salespersonFormControlValue?.salespersonId;
      }
      this.inlineEditService.saveInlineEdit(this.objectType, [this.value, salespersonFieldValue]);
    }
  }
}
